<template>
  <div class="tenantManagementContainer">
    <DeleteBlueprintConfirmationModal :show="showDeleteModal" @close="closeDeleteModal" @confirm="confirmDeleteBlueprint" />
    <div v-if="!readPermission" class="configurePageContainer" style="height: 100%; width: 100%">
      <PermissionAlert />
    </div>
    <div v-else class="configurePageContainer" style="height: 100%; width: 100%">
      <!-- Global loading spinner -->
      <div v-if="isLoading" style="position: absolute; top: 50%; left: 65%; transform: translate(-50%, -50%); display: flex; justify-content: center; align-items: center">
        <div class="spinner-border text-primary spinner-grow-xl" roleCategories="status"></div>
      </div>

      <div v-if="!showAddForm" class="customRowTenant">
        <p class="jobHistoryTitle" style="text-align: left">{{ $t('documentTypes.title') }}</p>
        <div class="searchBar">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <circle cx="6.5" cy="7.45996" r="5.75" stroke="#8F969B" stroke-width="1.5" />
            <path d="M11 11.96L15 15.96" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <input type="text" class="searchInput" :placeholder="$t('documentTypes.search_placeholder')" v-model="search" @input="handleSearch" />
        </div>
      </div>

      <!-- Search & Add Button -->
      <div v-if="!showAddForm" class="tableHeadingsNew">
        <div class="tableHeadingsNew" style="margin-top: 20px">
          <div class="customRow">
            <CustomDropdown :label="$t('documentTypes.select_tenant')" :placeholder="$t('documentTypes.select_tenant')" v-model="selectedTenantId" :options="tenantsWithMaster" :error="roleError" disabled />
            <img src="/backward.svg" :alt="$t('documentTypeActions.back')" style="cursor: pointer" @click="goToPreviousPage" />
            <span class="pageOfTitle">{{ $t('documentTypes.pagination.page') }} {{ page }} {{ $t('documentTypes.pagination.of') }} {{ totalPages }}</span>
            <img src="/forward.svg" :alt="$t('documentTypeActions.next')" style="cursor: pointer" @click="goToNextPage" />
            <div class="pageDiv" @click.stop="togglePageSizeDropdown">
              <span class="pageDivText">{{ pageSize }}</span>
              <img src="/arrowDownNew.png" class="arrowDownNew" alt="" />

              <ul ref="dropdownEl" v-if="showPageSizeDropdown" class="pageSizeDropdownNew" @click.stop>
                <li v-for="size in pageSizeOptions" :key="size" @click.stop="selectPageSize(size)">
                  {{ size }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div style="display: flex; gap: 17px">
          <Filter v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract" :tenant-id="selectedTenantId" @filter-applied="handleFilterChange" />
          <SecondaryButton v-if="writePermission && !isLoading && ((userRole === 'Super Admin' && !selectedTenantUsesBedrock && !selectedTenantUsesTextract) || userRole === 'Tenant Admin')" @click="selectedTenantUsesBedrock || selectedTenantUsesTextract ? handleAddDocumentWithBedrock() : handleAddDocumentType()">
            {{ $t('documentTypes.add_button') }}
          </SecondaryButton>
        </div>
      </div>

      <!-- Add/Edit Document Type Form -->
      <div v-if="showAddForm && writePermission" class="createDocumentTypeBody">
        <div class="createDocumentTypeHeader">
          <svg @click="cancelAdd" style="cursor: pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
            <path d="M5.25 11.25H20.25C20.45 11.25 20.64 11.33 20.78 11.47C20.92 11.61 21 11.8 21 12C21 12.2 20.92 12.39 20.78 12.53C20.64 12.67 20.45 12.75 20.25 12.75H5.25C5.05 12.75 4.86 12.67 4.72 12.53C4.58 12.39 4.5 12.2 4.5 12C4.5 11.8 4.58 11.61 4.72 11.47C4.86 11.33 5.05 11.25 5.25 11.25Z" fill="black" />
            <path d="M5.56 12L11.78 18.22C11.92 18.36 12 18.55 12 18.75C12 18.95 11.92 19.14 11.78 19.28C11.64 19.42 11.45 19.5 11.25 19.5C11.05 19.5 10.86 19.42 10.72 19.28L3.97 12.53C3.9 12.46 3.84 12.38 3.81 12.29C3.77 12.2 3.75 12.1 3.75 12C3.75 11.9 3.77 11.8 3.81 11.71C3.84 11.62 3.9 11.54 3.97 11.47L10.72 4.72C10.86 4.58 11.05 4.5 11.25 4.5C11.45 4.5 11.64 4.58 11.78 4.72C11.92 4.86 12 5.05 12 5.25C12 5.45 11.92 5.64 11.78 5.78L5.56 12Z" fill="black" />
          </svg>
          <span class="newPageTitle">{{ editMode ? $t('documentTypes.forms.edit_title') : $t('documentTypes.forms.add_title') }}</span>
        </div>
        <!-- :disabled="editMode && newDocumentType.tenantId" -->
        <div class="form-grid">
          <div class="form-field" v-show="!editMode || (editMode && newDocumentType.tenantId)">
            <label>{{ $t('documentTypes.forms.labels.tenant_name') }}</label>
            <select v-model="newDocumentType.tenantId" :class="{ 'error-border': showErrors && errors.tenantId }" :disabled="shouldDisableTenantDropdown">
              <option value="" :disabled="selectedTenantId !== 'master'">{{ selectedTenantId === 'master' ? $t('documentTypes.forms.options.master_tenant') : $t('documentTypes.forms.placeholders.select_tenant') }}</option>
              <option v-for="tenant in tenants" :value="tenant.value" :key="tenant.value">
                {{ tenant.label }}
              </option>
            </select>
            <span class="error-text" v-if="showErrors && errors.tenantId">
              {{ errors.tenantId }}
            </span>
          </div>
        </div>
        <div class="form-sections">
          <!-- Categorization Section -->
          <div class="form-section">
            <div class="section-header">
              <h3 class="section-title">{{ $t('documentTypes.forms.sections.categorization.title') }}</h3>
              <p class="section-description">{{ $t('documentTypes.forms.sections.categorization.description') }}</p>
            </div>
            <div class="section-content">
              <div class="form-grid">
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.primary_classification') }}</label>
                  <select
                    v-model="newDocumentType.category_id"
                    :class="{
                      'error-border': showErrors && errors.category_id,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_primary_classification') }}</option>
                    <option v-for="category in filteredActiveCategories" :value="category.id" :key="category.id">
                      {{ category.category_name }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.category_id">
                    {{ errors.category_id }}
                  </span>
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.sub_category_name') }}</label>
                  <select
                    v-model="newDocumentType.department_id"
                    :class="{
                      'error-border': showErrors && errors.department_id,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_sub_category') }}</option>
                    <option v-for="dept in activeDepartments" :value="dept.id" :key="dept.id">
                      {{ dept.department_name }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.department_id">
                    {{ errors.department_id }}
                  </span>
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.document_type') }}</label>
                  <template v-if="showVasionFields">
                    <select
                      v-model="newDocumentType.document_type_id"
                      :class="{
                        'error-border': showErrors && errors.document_type_id,
                      }"
                    >
                      <option value="">{{ $t('documentTypes.forms.placeholders.select_document_type') }}</option>
                      <option v-for="type in tenantDocumentTypes" :value="type.id" :key="type.id">
                        {{ type.document_type_name }}
                      </option>
                    </select>
                    <span class="error-text" v-if="showErrors && errors.document_type_id">
                      {{ errors.document_type_id }}
                    </span>
                  </template>
                  <template v-else>
                    <input
                      type="text"
                      v-model="newDocumentType.document_type_name"
                      :class="{
                        'error-border': showErrors && errors.document_type_name,
                      }"
                      :placeholder="$t('documentTypes.forms.placeholders.enter_document_type')"
                    />
                    <span class="error-text" v-if="showErrors && errors.document_type_name">
                      {{ errors.document_type_name }}
                    </span>
                  </template>
                </div>
                <div class="form-field" v-if="showVasionFields">
                  <label>{{ $t('documentTypes.forms.labels.document_type_display_name') }}</label>
                  <input
                    type="text"
                    v-model="newDocumentType.document_type_display_name"
                    :class="{
                      'error-border': showErrors && errors.document_type_display_name,
                    }"
                    :placeholder="$t('documentTypes.forms.placeholders.enter_display_name')"
                  />
                  <span class="error-text" v-if="showErrors && errors.document_type_display_name">
                    {{ errors.document_type_display_name }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Vasion Configuration Section -->
          <div class="form-section" v-if="showVasionFields">
            <div class="section-header">
              <h3 class="section-title">{{ $t('documentTypes.forms.sections.vasion_configuration.title') }}</h3>
              <p class="section-description">{{ $t('documentTypes.forms.sections.vasion_configuration.description') }}</p>
            </div>
            <div class="section-content">
              <div class="form-grid">
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.vasion_form_name') }}</label>
                  <select
                    v-model="newDocumentType.vasion_form_id"
                    :class="{
                      'error-border': showErrors && errors.vasion_form_id,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_vasion_form') }}</option>
                    <option v-for="form in vasionForms" :value="form.id" :key="form.id">
                      {{ form.vasion_form_id }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.vasion_form_id">
                    {{ errors.vasion_form_id }}
                  </span>
                </div>

                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.vasion_workflow_name') }}<span v-if="!newDocumentType.send_to_vasion_storage" class="required">*</span></label>
                  <select
                    v-model="newDocumentType.vasion_workflow_id"
                    :class="{
                      'error-border': showErrors && errors.vasion_workflow_id,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_workflow') }}</option>
                    <option v-for="workflow in workflows" :value="workflow.iID" :key="workflow.iID">
                      {{ workflow.sName }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.vasion_workflow_id">
                    {{ errors.vasion_workflow_id }}
                  </span>
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.vasion_approved_folder') }}</label>
                  <select
                    v-model="newDocumentType.vasion_folder_id"
                    :class="{
                      'error-border': showErrors && errors.vasion_folder_id,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_approved_folder') }}</option>
                    <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                      {{ folder.Name }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.vasion_folder_id">
                    {{ errors.vasion_folder_id }}
                  </span>
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.vasion_rejection_folder') }}</label>
                  <select
                    v-model="newDocumentType.vasion_rejection_folder_id"
                    :class="{
                      'error-border': showErrors && errors.vasion_rejection_folder_id,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_rejection_folder') }}</option>
                    <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                      {{ folder.Name }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.vasion_rejection_folder_id">
                    {{ errors.vasion_rejection_folder_id }}
                  </span>
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.vasion_review_folder') }}</label>
                  <select
                    v-model="newDocumentType.vasion_review_folder_id"
                    :class="{
                      'error-border': showErrors && errors.vasion_review_folder_id,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_approval_folder') }}</option>
                    <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                      {{ folder.Name }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.vasion_review_folder_id">
                    {{ errors.vasion_review_folder_id }}
                  </span>
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.vasion_failed_documents_folder') }}</label>
                  <select
                    v-model="newDocumentType.vasion_failed_folder_id"
                    :class="{
                      'error-border': showErrors && errors.vasion_failed_folder_id,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_failed_documents_folder') }}</option>
                    <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                      {{ folder.Name }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.vasion_failed_folder_id">
                    {{ errors.vasion_failed_folder_id }}
                  </span>
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.default_automatic_vasion_integration') }}</label>
                  <select v-model="newDocumentType.send_to_vasion_storage" class="custom-select">
                    <option :value="true">{{ $t('documentTypes.forms.options.send_to_vasion_storage') }}</option>
                    <option :value="false">{{ $t('documentTypes.forms.options.start_vasion_workflow') }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- General Settings Section -->
          <div class="form-section">
            <div class="section-header">
              <h3 class="section-title">{{ $t('documentTypes.forms.sections.model_configuration.title') }}</h3>
              <p class="section-description">{{ $t('documentTypes.forms.sections.model_configuration.description') }}</p>
            </div>
            <div class="section-content">
              <div class="form-grid">
                <div v-if="!shouldShowBedrockFields && this.newDocumentType.tenantId == ''" class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.model_name') }}</label>
                  <select v-model="newDocumentType.model_id" :class="{ 'error-border': showErrors && errors.model_id }">
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_model') }}</option>
                    <option v-for="model in activeModels" :value="model.id" :key="model.id">
                      {{ model.classification_model_name }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.model_id">
                    {{ errors.model_id }}
                  </span>
                </div>
                <div v-if="newDocumentType.bda_blueprint && !shouldShowBedrockFields" class="form-field">
                  <label> IDP Document Class </label>
                  <input type="text" readonly name="" id="newDocumentType.bda_blueprint" v-model="newDocumentType.bda_blueprint" class="readonly-input" />
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.ml_confidence_level_threshold') }}</label>
                  <select
                    v-model="newDocumentType.ml_confidence_level"
                    :class="{
                      'error-border': showErrors && errors.ml_confidence_level,
                    }"
                  >
                    <option value="">{{ $t('documentTypes.forms.placeholders.select_threshold') }}</option>
                    <option v-for="threshold in thresholds" :value="threshold.value" :key="threshold.value">
                      {{ threshold.label }}
                    </option>
                  </select>
                  <span class="error-text" v-if="showErrors && errors.ml_confidence_level">
                    {{ errors.ml_confidence_level }}
                  </span>
                </div>
                <div class="form-field">
                  <label>{{ $t('documentTypes.forms.labels.status') }}</label>
                  <IsActiveComponentV1 v-model="newDocumentType.is_active" @change="handleStatusChange" />
                </div>
                <div class="form-field" v-if="newDocumentType.tenantId !== ''">
                  <label>{{ $t('documentTypes.forms.labels.manual_validation') }}</label>
                  <IsActiveComponentV1 :label="$t('documentTypes.forms.labels.manual_validation')" v-model="newDocumentType.requires_complete_manual_validation" @change="handleManualValidationFlagChange" />
                </div>
                <!-- <div class="form-field" v-if="newDocumentType.tenantId">
                  <label>{{ $t('documentTypes.forms.labels.split_by_page_number') }}</label>
                  <input
                    type="text"
                    v-model="newDocumentType.page_count"
                    :class="{
                      'error-border': showErrors && errors.page_count,
                    }"
                    :placeholder="$t('documentTypes.forms.placeholders.enter_split_page_number')"
                  />
                </div> -->
              </div>
            </div>
          </div>
          <!-- Splitting Configuration Section -->
          <div v-if="selectedTenantId !== 'master'"" class="form-section">
            <div class="section-header">
              <h3 class="section-title">{{ $t('configureVasionSettings.splittingConfiguration') }}</h3>
              <p class="section-description">{{ $t('configureVasionSettings.splittingConfigurationDescription') }}</p>
            </div>
            <div class="section-content">
              <div class="splitting-options">
                <!-- Auto Option -->
                <div class="radio-option">
                  <input type="radio" id="detection-auto" value="auto" v-model="newDocumentType.detection_mode" class="radio-input" />
                  <label for="detection-auto" class="radio-label">{{ $t('configureVasionSettings.auto') }}</label>
                </div>

                <!-- Do not delete below commented code - required for qr splitting functionality -->
                <!-- QR Code Option -->
                <div class="radio-option">
                  <input type="radio" id="detection-qr" value="qr" v-model="newDocumentType.detection_mode" class="radio-input" />
                  <label for="detection-qr" class="radio-label">QR Code</label>
                </div>

                <!-- QR Code Splitting Strategy Options -->
                <div v-if="newDocumentType.detection_mode === 'qr'" class="conditional-field">
                  <div class="sub-options">
                    <p class="sub-options-title">Select splitting strategy:</p>
                    <div class="radio-option">
                      <input type="radio" id="qr-strategy-static" value="static_code_splitting" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                      <label for="qr-strategy-static" class="radio-label">Static Splitting</label>
                    </div>

                    <div class="radio-option">
                      <input type="radio" id="qr-strategy-dynamic" value="dynamic_code_splitting" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                      <label for="qr-strategy-dynamic" class="radio-label">Dynamic Splitting With Variable Values</label>
                    </div>

                    <div class="radio-option">
                      <input type="radio" id="qr-strategy-separator" value="break_on_code_as_separator" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                      <label for="qr-strategy-separator" class="radio-label">Splitter Page (Splitter page is only a marker and won't appear in final files)</label>
                    </div>
                    <span class="error-text" v-if="showErrors && errors.splitting_strategy && newDocumentType.detection_mode === 'qr'">
                      {{ errors.splitting_strategy }}
                    </span>
                  </div>
                </div>

                <!-- Barcode Option -->
                <div class="radio-option">
                  <input type="radio" id="detection-barcode" value="barcode" v-model="newDocumentType.detection_mode" class="radio-input" />
                  <label for="detection-barcode" class="radio-label">Barcode</label>
                </div>

                <!-- Barcode Splitting Strategy Options -->
                <div v-if="newDocumentType.detection_mode === 'barcode'" class="conditional-field">
                  <div class="sub-options">
                    <p class="sub-options-title">Select splitting strategy:</p>
                    <div class="radio-option">
                      <input type="radio" id="barcode-strategy-static" value="static_code_splitting" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                      <label for="barcode-strategy-static" class="radio-label">Static Splitting</label>
                    </div>

                    <div class="radio-option">
                      <input type="radio" id="barcode-strategy-dynamic" value="dynamic_code_splitting" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                      <label for="barcode-strategy-dynamic" class="radio-label">Dynamic Splitting With Variable Values</label>
                    </div>

                    <div class="radio-option">
                      <input type="radio" id="barcode-strategy-separator" value="break_on_code_as_separator" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                      <label for="barcode-strategy-separator" class="radio-label">Splitter Page (Splitter page is only a marker and won't appear in final files)</label>
                    </div>
                    <span class="error-text" v-if="showErrors && errors.splitting_strategy && newDocumentType.detection_mode === 'barcode'">
                      {{ errors.splitting_strategy }}
                    </span>
                  </div>
                </div>

                <!-- Page Count Option -->
                <div class="radio-option">
                  <input type="radio" id="detection-page-count" value="page_count" v-model="newDocumentType.detection_mode" class="radio-input" />
                  <label for="detection-page-count" class="radio-label">{{ $t('configureVasionSettings.pageCount') }}</label>
                </div>

                <!-- Page Count Input -->
                <div v-if="newDocumentType.detection_mode === 'page_count'" class="conditional-field">
                  <div class="form-field">
                    <label>{{ $t('configureVasionSettings.enterPageCount') }}</label>
                    <input
                      type="number"
                      v-model.number="newDocumentType.page_number"
                      :class="{
                        'error-border': showErrors && errors.page_number,
                      }"
                      :placeholder="$t('configureVasionSettings.enterPageCountPlaceholder')"
                      min="1"
                    />
                    <span class="error-text" v-if="showErrors && errors.page_number">
                      {{ errors.page_number }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="button-group">
          <button class="cancel-btn" @click="cancelAdd">{{ $t('documentTypes.buttons.cancel') }}</button>
          <button class="save-btn" @click="saveDocumentType" :disabled="loadingStates.saveDocumentType">
            {{ loadingStates.saveDocumentType ? $t('documentTypes.buttons.saving') : $t('documentTypes.buttons.save') }}
          </button>
        </div>
      </div>

      <!-- Document Types Table -->
      <div v-if="!showAddForm && !isLoading" class="tableContainer" style="margin-top: 20px">
        <table class="customTable">
          <thead class="customTableHeader" v-if="!loadingStates.documentTypes">
            <tr class="customTableRow">
              <th>{{ $t('documentTypes.table.sr_no') }}</th>
              <th>{{ $t('documentTypes.table.tenant_name') }}</th>
              <th v-if="selectedTenantUsesBedrock || selectedTenantUsesTextract">{{ $t('documentTypes.table.source_file_name') }}</th>
              <th v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract">{{ $t('documentTypes.table.primary_classification') }}</th>
              <th v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract">{{ $t('documentTypes.table.sub_category') }}</th>
              <th>{{ $t('documentTypes.table.document_type_name') }}</th>
              <th v-if="selectedTenantUsesBedrock">{{ $t('documentTypes.table.classified_as') }}</th>
              <th v-if="selectedTenantUsesTextract">Analysis Type</th>
              <th v-if="selectedTenantUsesBedrock || selectedTenantUsesTextract">{{ $t('documentTypes.table.status') }}</th>
              <!-- <th>Send To Vasion Storage</th> -->
              <th v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract">{{ $t('documentTypes.table.is_active') }}</th>
              <th v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract">{{ $t('documentTypes.table.action') }}</th>
              <th class="text-center" v-if="(selectedTenantUsesBedrock || selectedTenantUsesTextract) && selectedTenantId !== 'master'">{{ $t('documentTypeActions.trainingActions') }}</th>
            </tr>
          </thead>
          <tbody class="customTableBody">
            <!-- No data message -->
            <tr v-if="!loadingStates.documentTypes && (!filteredDocumentTypes || filteredDocumentTypes.length === 0)" style="height: 50px !important">
              <td :colspan="getTableColumnCount()" style="text-align: center; color: #666; font-style: italic">
                {{ $t('documentTypes.no_data_available') || 'No data available' }}
              </td>
            </tr>
            <!-- Data rows -->
            <tr v-else v-for="(docType, index) in filteredDocumentTypes" :key="docType.id" style="height: 50px !important">
              <td>{{ index + 1 }}</td>
              <td>{{ docType?.tenant?.tenant_name || 'NA' }}</td>
              <td v-if="selectedTenantUsesBedrock || selectedTenantUsesTextract">{{ docType?.source_file_name ?? '----' }}</td>
              <td v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract">{{ docType?.category?.category_name }}</td>
              <td v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract" class="truncate-cell">
                <div class="tooltip-container">
                  <span class="truncate-text">{{ docType.department.department_name }}</span>
                  <div class="tooltip">
                    {{ docType.department.department_name }}
                  </div>
                </div>
              </td>
              <td class="truncate-cell">
                <div class="tooltip-container">
                  <span class="truncate-text">
                    {{ formatString(docType?.document_type_display_name || docType?.document_type_name) }}
                  </span>
                  <div class="tooltip">
                    {{ formatString(docType?.document_type_display_name || docType?.document_type_name) }}
                  </div>
                </div>
              </td>
              <td v-if="selectedTenantUsesBedrock" class="truncate-cell">
                <div class="tooltip-container">
                  <span class="truncate-text">
                    {{ getUserReadableName(docType?.bedrock_class_name) || docType.document_type_display_name }}
                  </span>
                  <div class="tooltip">
                    {{ getUserReadableName(docType?.bedrock_class_name) || docType.document_type_display_name }}
                  </div>
                </div>
              </td>
              <td v-if="selectedTenantUsesTextract" class="truncate-cell">
                <div class="tooltip-container">
                  <span class="truncate-text">
                    {{ docType?.bda_blueprint || '----' }}
                  </span>
                  <div class="tooltip">
                    {{ docType?.bda_blueprint || '----' }}
                  </div>
                </div>
              </td>
              <td v-if="selectedTenantUsesBedrock || selectedTenantUsesTextract" class="truncate-cell">
                <span class="status-badge" :class="docType?.is_published ? 'status-published' : 'status-draft'">
                  {{ docType?.is_published ? 'Published' : 'Draft' }}
                </span>
              </td>
              <!-- <td>
                <div class="toggle-wrapper" v-if="docType.tenant_id">
                  <CustomToggle
                    :modelValue="docType.send_to_vasion_storage"
                    @update:modelValue="
                      (value) => handleVasionStorageToggle(docType, value)
                    "
                  />
                </div>
                <span v-else>{{ $t('documentTypeActions.notAvailable') }}</span>
              </td> -->
              <td v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract">
                <div class="toggle-wrapper">
                  <CustomToggle v-if="writePermission" :modelValue="docType.is_active" @update:modelValue="(value) => handleStatusToggle(docType, value)" />
                  <span v-else>{{ docType.is_active ? $t('documentTypes.status.active') : $t('documentTypes.status.inactive') }}</span>
                </div>
              </td>
              <td v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract">
                <CustomHyperlink v-if="writePermission" @click="editDocumentType(docType)">{{ $t('documentTypes.actions.edit') }}</CustomHyperlink>
              </td>
              <td v-if="(selectedTenantUsesBedrock || selectedTenantUsesTextract) && selectedTenantId !== 'master'" style="text-align: center">
                <!-- Show Train Document label only when NOT using Bedrock/Textract AND blueprint is empty -->
                <CustomHyperlink v-if="!selectedTenantUsesBedrock && !selectedTenantUsesTextract && (docType?.bda_blueprint === null || docType?.bda_blueprint === undefined || docType?.bda_blueprint === '')" @click="addBlueprint(docType)">Train Document</CustomHyperlink>
                <!-- Show 3-dot menu when using Bedrock/Textract OR when blueprint exists -->
                <div v-else-if="writePermission" class="actions-menu" @click.stop>
                  <button class="actions-button" @click="showActionsMenu($event, docType)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="6" r="2" fill="var(--primary-color)" />
                      <circle cx="12" cy="12" r="2" fill="var(--primary-color)" />
                      <circle cx="12" cy="18" r="2" fill="var(--primary-color)" />
                    </svg>
                  </button>
                  <div v-if="showActionsDropdown && selectedDocumentType?.id === docType?.id" class="actions-dropdown" :class="{ 'actions-dropup': shouldShowDropup }">
                    <!-- <div class="action-item" @click="viewBlueprint(docType)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M21.544 11.045C21.848 11.471 22 11.685 22 12C22 12.316 21.848 12.529 21.544 12.955C20.178 14.871 16.689 19 12 19C7.31 19 3.822 14.87 2.456 12.955C2.152 12.529 2 12.315 2 12C2 11.684 2.152 11.471 2.456 11.045C3.822 9.129 7.311 5 12 5C16.69 5 20.178 9.13 21.544 11.045Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9C11.2044 9 10.4413 9.31607 9.87868 9.87868C9.31607 10.4413 9 11.2044 9 12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9C11.2044 9 10.4413 9.31607 9.87868 9.87868C9.31607 10.4413 9 11.2044 9 12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                      <span>{{ docType?.bda_blueprint != '' ? 'View' : 'Add' }}</span>
                    </div> -->
                    <div class="action-item" @click="editBlueprint(docType)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M18.5 2.50023C18.8978 2.1025 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1025 21.5 2.50023C21.8978 2.89804 22.1214 3.43762 22.1214 4.00023C22.1214 4.56284 21.8978 5.10242 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                      <span>{{ docType?.bda_blueprint === '' ? $t('documentTypeActions.add') : $t('documentTypeActions.edit') }}</span>
                    </div>
                    <div class="action-item" @click="deleteBlueprint(docType)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                      <span>{{ $t('documentTypeActions.delete') }}</span>
                    </div>
                  </div>
                </div>
                <span v-else>{{ $t('documentTypeActions.notAvailable') }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <ConfirmationModal :show="showConfirmModal" :message="getConfirmationMessage()" @close="closeConfirmModal" @confirm="confirmStatusChange" />
  </div>
</template>

<script>
import { get, post, put, del } from '@/utils/api'
import { showToast } from '@/utils/toast'
import CustomToggle from '@/components/CustomToggle/CustomToggle.vue'
import CustomHyperlink from '@/components/CustomHyperlink/CustomHyperlink.vue'
import SecondaryButton from '@/components/SecondaryButton/SecondaryButton.vue'
import Filter from './Filter.vue'
import CustomDropdown from '@/components/CustomDropdown/CustomDropdown.vue'
import IsActiveComponentV1 from '@/components/IsActiveComponentV1/IsActiveComponentV1.vue'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue'
import PermissionAlert from '@/components/PermissionAlert.vue'
import DeleteBlueprintConfirmationModal from '@/components/DeleteBlueprintConfirmationModal/DeleteBlueprintConfirmationModal.vue'
import tenantSelectionStore from '@/stores/tenantSelectionStore'

export default {
  components: {
    CustomToggle,
    CustomHyperlink,
    SecondaryButton,
    Filter,
    CustomDropdown,
    IsActiveComponentV1,
    ConfirmationModal,
    PermissionAlert,
    DeleteBlueprintConfirmationModal,
  },
  data() {
    return {
      loadingStates: {
        documentTypes: false,
        categories: false,
        tenants: false,
        saveDocumentType: false,
        departments: false,
        models: false,
        vasionForms: false,
        vasionWorkflows: false,
        vasionFolders: false,
        tenantCategories: false,
        tenantDocumentTypes: false,
        blueprintDetails: false,
      },
      showDeleteModal: false,
      blueprintToDelete: null,
      blueprintToDeleteName: null,
      showActionsDropdown: false,
      shouldShowDropup: false,
      documentTypes: [],
      categories: [],
      tenants: [],
      tenantsWithMaster: [],
      userRole: localStorage.getItem('userRole'),
      departments: [],
      blueprintDetails: [],
      tenantDetails: [],
      selectedTenantId: '',
      selectedTenantUsesBedrock: false,
      selectedTenantUsesTextract: false,
      loadingTenantAiMethod: false,
      tenantStoreUnsubscribe: null,
      roleError: '',
      search: '',
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
      pageSizeOptions: [10, 20, 50, 100],
      showPageSizeDropdown: false,
      showAddForm: false,
      showConfirmModal: false,
      editMode: false,
      editId: null,
      selectedDocumentType: null,
      newDocumentType: {
        tenantId: '',
        category_id: '',
        department_id: '',
        vasion_form_id: '',
        model_id: null,
        document_type_name: '',
        document_type_id: '',
        document_type_display_name: '',
        ml_confidence_level: '',
        vasion_workflow_id: null,
        vasion_folder_id: '',
        vasion_rejection_folder_id: '',
        vasion_review_folder_id: '',
        vasion_failed_folder_id: '',
        send_to_vasion_storage: false,
        is_active: true,
        created_by: null,
        page_count: 0,
        requires_complete_manual_validation: false,
        bedrock_class_name: '',
        bda_blueprint: '',
        bda_invocation_arn: null,
        detection_mode: 'auto', // API key: auto, qr, barcode, page_count
        splitting_strategy: '', // API key: static_code_splitting, dynamic_code_splitting, break_on_code_as_separator
        page_number: '', // Used when detection_mode is 'page_count'
      },
      vasionForms: [],
      models: [],
      displayNames: [],
      thresholds: [
        { label: '50%', value: 50 },
        { label: '60%', value: 60 },
        { label: '70%', value: 70 },
        { label: '80%', value: 80 },
        { label: '90%', value: 90 },
        { label: '100%', value: 100 },
      ],
      workflows: [],
      storageFolders: [],
      isPopulating: false,
      showErrors: false,
      errors: {},
      pendingStatusChange: null,
      pendingVasionStorageChange: null,
      activeFilters: null,
      readPermission: false,
      writePermission: false,
      readPermissionMaster: false,
      writePermissionMaster: false,
    }
  },
  computed: {
    // Filtering is now handled by backend API using include_bedrock parameter
    filteredDocumentTypes() {
      return this.documentTypes || []
    },
    isLoading() {
      return Object.values(this.loadingStates).some((state) => state) || this.loadingTenantAiMethod
    },
    activeCategories() {
      return this.categories.filter((category) => category.is_active === true || category.id === this.newDocumentType.category_id)
    },
    filteredActiveCategories() {
      return this.activeCategories.filter((category) => category && category.category_name !== 'Bedrock Training' && category.category_name !== 'Bedrock Documents' &&  category.category_name !== 'Textract Training')
    },
    activeDocumentTypes() {
      return (this.documentTypes || []).filter((doc) => doc.is_active === true || doc.id === this.newDocumentType.document_type_id)
    },
    activeDepartments() {
      return (this.departments || []).filter((dept) => dept.is_active === true || dept.id === this.newDocumentType.department_id)
    },
    activeModels() {
      return (this.models || []).filter((model) => model.is_active === true || model.id === this.newDocumentType.model_id)
    },

    showVasionFields() {
      return this.newDocumentType.tenantId !== ''
    },
    showModelField() {
      return !this.newDocumentType.tenantId
    },
    shouldShowBedrockFields() {
      // For tenant-specific document types, check the selected tenant's AI method
      if (this.newDocumentType.tenantId) {
        return this.selectedTenantUsesBedrock
        // return true
      } else {
        return false
        // return true
      }
    },
    shouldDisableTenantDropdown() {
      // Always disable tenant dropdown for consistency with list view
      // The tenant is automatically set from the tenant selection store
      return true
    },
  },
  async mounted() {
    document.title = this.$t('documentTypes.title')
    this.checkPermissions()
    this.fetchTenantDetails()

    // FIRST: Get current state from store BEFORE fetching tenants
    const storeState = tenantSelectionStore.getCurrentState()

    this._isStoreUpdate = true
    this.selectedTenantId = storeState.selectedTenantId
    this.selectedTenantUsesBedrock = storeState.selectedTenantUsesBedrock
    this.selectedTenantUsesTextract = storeState.selectedTenantUsesTextract
    this.loadingTenantAiMethod = storeState.loadingTenantAiMethod
    this._isStoreUpdate = false

    // THEN: Fetch tenants to populate dropdown (won't override store value now)
    await this.fetchTenants()

    // Subscribe to tenant selection store
    this.tenantStoreUnsubscribe = tenantSelectionStore.subscribe((state) => {
      const previousTenantId = this.selectedTenantId

      // Set flag to prevent watcher from triggering store update
      this._isStoreUpdate = true
      this.selectedTenantId = state.selectedTenantId
      this.selectedTenantUsesBedrock = state.selectedTenantUsesBedrock
      this.selectedTenantUsesTextract = state.selectedTenantUsesTextract
      this.loadingTenantAiMethod = state.loadingTenantAiMethod
      this._isStoreUpdate = false

      // Only fetch data if tenant actually changed and it's not the initial load
      if (previousTenantId !== state.selectedTenantId && previousTenantId !== '') {
        this.fetchDocumentTypes()
      }
    })

    // Fetch document types with the current tenant
    this.fetchDocumentTypes()
    this.fetchModels()
    document.addEventListener('click', this.handleClickAway)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
    // Unsubscribe from tenant selection store
    if (this.tenantStoreUnsubscribe) {
      this.tenantStoreUnsubscribe()
    }
  },
  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')

      this.readPermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_RD' && p.is_active)
      this.writePermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_WR' && p.is_active)
      this.readPermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_WR' && p.is_active)
    },
    getUserReadableName(input) {
      if (input) {
        return input.replaceAll('_', ' ').replaceAll('-', ' ')
      } else {
        return input
      }
    },
    deleteBlueprint(blueprint) {
      this.blueprintToDelete = blueprint?.bda_blueprint
      this.blueprintToDeleteName = blueprint?.id
      this.showDeleteModal = true
      this.showActionsDropdown = false
      this.shouldShowDropup = false
    },
    formatString(input) {
      // Split only on the first two underscores
      if (!input) return ''
      const parts = input.split('_')

      // If fewer than 3 parts, nothing to format
      if (parts.length < 3) return input

      // Join everything after the second underscore
      const rest = parts.slice(2).join('_')

      // Replace _ and - with space
      return rest.replace(/[_-]/g, ' ')
    },
    viewBlueprint() {
      // TODO: Implement view blueprint functionality
      showToast({
        title: this.$t('documentTypeActions.info'),
        message: this.$t('documentTypeActions.viewBlueprintNotImplemented'),
        type: 'info',
      })
      this.showActionsDropdown = false
      this.shouldShowDropup = false
    },

    async editBlueprint(documentType) {
      this.showActionsDropdown = false
      this.shouldShowDropup = false
      // Navigate to edit flow - all data will be fetched from API
      const urlName = localStorage.getItem('url_name')
      this.$router.push(`/${urlName}/document-training?document_type_id=${documentType?.id}&tenant_id=${documentType?.tenant_id}&isEditFlow=true`)
    },
    addBlueprint(blueprint) {
      this.showActionsDropdown = false
      this.shouldShowDropup = false
      const urlName = localStorage.getItem('url_name')
      const encodedDisplayName = encodeURIComponent(blueprint?.document_type_display_name || '')
      this.$router.push(`/${urlName}/document-training?displayName=${encodedDisplayName}&blueprintArn=${blueprint?.bda_blueprint}&document_type_id=${blueprint?.id}&tenant_id=${blueprint?.tenant_id}`)
    },
    showActionsMenu(event, docType) {
      event.stopPropagation()
      this.selectedDocumentType = docType
      this.showActionsDropdown = !this.showActionsDropdown

      // Calculate if dropdown should appear as dropup
      if (this.showActionsDropdown) {
        const button = event.target
        const buttonRect = button.getBoundingClientRect()
        const windowHeight = window.innerHeight
        const dropdownHeight = 300 // Approximate height of dropdown
        const spaceBelow = windowHeight - buttonRect.bottom
        const spaceAbove = buttonRect.top

        // If there's not enough space below but enough space above, show as dropup
        this.shouldShowDropup = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight
      } else {
        // Reset dropup flag when closing dropdown
        this.shouldShowDropup = false
      }
    },

    closeDeleteModal() {
      this.showDeleteModal = false
      this.blueprintToDelete = null
      this.blueprintToDeleteName = null
    },
    async confirmDeleteBlueprint() {
      // if (!this.blueprintToDelete) return
      try {
        let response

        if (this.selectedTenantUsesBedrock) {
          // For Bedrock tenants, use the Bedrock API that deletes from all tables
          response = await del(`/v1/bedrock/delete-training-data?tenantDocumentTypeId=${this.blueprintToDeleteName}`)
        } else if (this.selectedTenantUsesTextract) {
          // For Textract tenants, use the Textract API that deletes from all tables
          response = await del(`/v1/textract/delete-training-data?documentTypeId=${this.blueprintToDeleteName}`)
        } else {
          // For non-Bedrock/non-Textract tenants, use the existing blueprint deletion flow
          response = await del(`/v1/bedrock/delete-blueprint?tenantId=${this.selectedTenantId}&blueprintArn=${this?.blueprintToDelete}`)

          if (response?.status === 200) {
            // Update the tenant document type to clear blueprint fields
            const tenantDocumentTypeRes = await get(`/v1/tenant-document-types/${this.blueprintToDeleteName}`)
            if (tenantDocumentTypeRes.data && tenantDocumentTypeRes.data.data) {
              tenantDocumentTypeRes.data.data = {
                ...tenantDocumentTypeRes.data.data,
                bda_blueprint: null,
                bedrock_class_name: null,
                bda_invocation_arn: null,
              }
            }

            await put(`/v1/tenant-document-types/${this.blueprintToDeleteName}`, tenantDocumentTypeRes.data?.data)
          }
        }

        if (response?.status === 200) {
          showToast({
            title: this.$t('documentTypeActions.success'),
            message: this.$t('documentTypeActions.deletedSuccessfully'),
            type: 'success',
          })

          // Refresh the document types list
          this.fetchDocumentTypes()

          // Refresh blueprint details if using Bedrock
          if (this.selectedTenantUsesBedrock) {
            this.fetchBluePrintDetails()
          }
        } else {
          throw new Error(response?.data?.message || this.$t('documentTypeActions.failedToDelete'))
        }
      } catch (err) {
        showToast({
          title: this.$t('documentTypeActions.error'),
          message: err.message || this.$t('documentTypeActions.failedToDelete'),
          type: 'error',
        })
      } finally {
        this.closeDeleteModal()
      }
    },
    async fetchTenantDetails() {
      try {
        const tenantDetails = localStorage.getItem('TenantDetails')
        if (tenantDetails) {
          this.tenantDetails = JSON.parse(tenantDetails)
          // Don't call checkTenantAiMethod() here as it overrides cached values
          // The component subscribes to tenantSelectionStore which handles this
          // If AI method is Bedrock, fetch blueprint details
          if (this.selectedTenantUsesBedrock) {
            this.fetchBluePrintDetails()
          }
        }
      } catch (err) {
        this.tenantDetails = []
      }
    },
    async fetchBluePrintDetails() {
      this.loadingStates.blueprintDetails = true
      try {
        if (!this.selectedTenantId) return
        // let url = `/v1/classes?tenant_id=${this.selectedTenant}&page=${this.page}&pageSize=${this.pageSize}`
        let url = `/v1/bedrock/list-blueprints?tenantId=${this.selectedTenantId}`
        // const response = await post('/v1/bda/getBluePrints', {
        //   projectArn: getTenantConfigValue('project_arn'),
        //   // blueprintArn: 'arn:aws:bedrock:us-east-1:692859933965:data-automation-project/79d3fa7dfb1e',
        // })
        const response = await get(url)

        this.blueprintDetails = response?.data?.blueprints?.sort((a, b) => a?.ClassName?.localeCompare(b?.ClassName))
      } catch (err) {
        this.blueprintDetails = []
      } finally {
        this.loadingStates.blueprintDetails = false
      }
    },
    handleAddClass() {
      const urlName = localStorage.getItem('url_name')
      this.$router.push(`/${urlName}/document-training`)
    },
    handleAddDocumentWithBedrock() {
      // Navigate to document-training page with tenant context
      const urlName = localStorage.getItem('url_name')
      // Pass tenant_id in the URL to maintain context
      if (this.selectedTenantUsesBedrock) {
        this.$router.push(`/${urlName}/document-training?tenant_id=${this.selectedTenantId}`)
      } else {
        this.$router.push(`/${urlName}/document-training?tenant_id=${this.selectedTenantId}&isTextract=true`)
      }
    },
    handleClickAway(event) {
      const dropdownEl = this.$refs.dropdownEl
      if (dropdownEl && !dropdownEl.contains(event.target)) {
        this.showPageSizeDropdown = false
        this.showEditCategoryDropdown = false
      }

      // Close actions dropdown when clicking outside
      const actionsMenu = document.querySelector('.actions-dropdown')
      const actionsButton = document.querySelector('.actions-button')
      if (this.showActionsDropdown && actionsMenu && !actionsMenu.contains(event.target) && actionsButton && !actionsButton.contains(event.target)) {
        this.showActionsDropdown = false
        this.shouldShowDropup = false
      }
    },
    handleSearch() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }

      this.debounceTimeout = setTimeout(() => {
        this.page = 1
        this.fetchDocumentTypes()
      }, 300)
    },
    async fetchCategories() {
      this.loadingStates.categories = true
      try {
        const response = await get('/v1/categories?page=1&pageSize=1000')
        if (response?.data?.code === 200) {
          this.categories = response.data.data.data
        }
      } catch (err) {
        // Error handled silently
      } finally {
        this.loadingStates.categories = false
      }
    },
    async fetchTenants() {
      this.loadingStates.tenants = true
      try {
        const res = await get('/v1/tenants?page=1&pageSize=1000')
        if (res?.data?.code === 200) {
          this.tenants = (res.data.data.tenants || []).map((tenant) => ({
            label: tenant.tenant_name,
            value: tenant.id,
          }))
          if (this.userRole === 'Super Admin') {
            this.tenantsWithMaster = [{ label: 'Master Tenant', value: 'master' }, ...this.tenants]
            // Only set selectedTenantId to 'master' if it hasn't been initialized by the store
            if (!this.selectedTenantId) {
              this.selectedTenantId = 'master'
            }
          } else {
            this.tenantsWithMaster = this.tenants
            // Only set selectedTenantId to first tenant if it hasn't been initialized by the store
            if (!this.selectedTenantId) {
              this.selectedTenantId = this?.tenants?.[0]?.value || 'master'
            }
          }
        }
      } catch (err) {
        // Error handled silently
      } finally {
        this.loadingStates.tenants = false
      }
    },
    async fetchDocumentTypes() {
      this.loadingStates.documentTypes = true
      try {
        let url = `/v1/document-types?page=${this.page}&pageSize=${this.pageSize}&isDeleted=true`
        if (this.selectedTenantId != 'master') {
          url = `v1/tenant-document-types?page=${this.page}&pageSize=${this.pageSize}&tenantId=${this.selectedTenantId}&isDeleted=false`
        }

        // Add ai_method_filter parameter based on tenant and user context
        const aiMethodFilter = this.getAiMethodFilterValue()
        url += `&ai_method_filter=${aiMethodFilter}`

        if (this.search) {
          url += `&search=${encodeURIComponent(this.search)}`
        }

        if (this.activeFilters) {
          Object.entries(this.activeFilters).forEach(([key, value]) => {
            if (value) {
              url += `&${key}=${value}`
            }
          })
        }

        const response = await get(url)
        if (response?.data?.code === 200) {
          this.documentTypes = response.data.data.data || []
          this.total = response.data.data.total || 0
          this.totalPages = Math.ceil(this.total / this.pageSize)
        } else {
          this.documentTypes = []
          this.total = 0
          this.totalPages = 1
        }
      } catch (err) {
        console.error('❌ DocumentType: Error fetching document types:', err)
        this.documentTypes = []
        this.total = 0
        this.totalPages = 1
      } finally {
        this.loadingStates.documentTypes = false
      }
    },
    async fetchDepartmentsByCategory(categoryId) {
      this.loadingStates.departments = true
      try {
        const url = `/v1/departments?categoryId=${categoryId}&page=1&pageSize=100000`
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.departments = response.data.data.data || []
        }
      } catch (err) {
        this.departments = []
      } finally {
        this.loadingStates.departments = false
      }
    },
    async fetchModels() {
      this.loadingStates.models = true
      try {
        const url = '/v1/models?page=1&pageSize=100000'
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.models = response.data.data.data || []
        }
      } catch (err) {
        this.models = []
      } finally {
        this.loadingStates.models = false
      }
    },
    async fetchTenantCategories(tenantId) {
      this.loadingStates.tenantCategories = true
      try {
        const response = await get(`/v1/tenant-categories?tenantId=${tenantId}&page=1&pageSize=100000`)
        if (response?.data?.code === 200) {
          this.categories = (response.data.data.data || []).map((category) => ({
            id: category.category_id,
            category_name: category.category_display_name,
            is_active: category.is_active,
          }))
        }
      } catch (err) {
        // Error handled silently
      } finally {
        this.loadingStates.tenantCategories = false
      }
    },
    async fetchTenantDepartments(tenantId, categoryId) {
      this.loadingStates.departments = true
      try {
        const url = `/v1/tenant-departments?tenantId=${tenantId}&categoryId=${categoryId}&page=1&pageSize=100000`
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.departments = (response.data.data.data || []).map((dept) => ({
            id: dept.department_id,
            department_name: dept.department_display_name,
            is_active: dept.is_active,
          }))
        }
      } catch (err) {
        this.departments = []
      } finally {
        this.loadingStates.departments = false
      }
    },
    async fetchTenantDocumentTypes(categoryId, departmentId) {
      this.loadingStates.tenantDocumentTypes = true
      try {
        const url = `/v1/document-types?page=1&pageSize=10000&categoryId=${categoryId}&departmentId=${departmentId}`
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.tenantDocumentTypes = (response.data.data.data || [])
            .filter((doc) => doc.is_active)
            .map((doc) => ({
              id: doc.id,
              document_type_name: doc?.document_type_name,
            }))
        }
      } catch (err) {
        // Error handled silently
      } finally {
        this.loadingStates.tenantDocumentTypes = false
      }
    },
    async fetchVasionForms(tenantId) {
      this.loadingStates.vasionForms = true
      try {
        const response = await get(`/v1/vasion/objects?tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          if (response.data.data && response.data.data.IndexForms) {
            this.vasionForms = Object.entries(response.data.data.IndexForms || []).map(([key, value]) => ({
              id: parseInt(key),
              vasion_form_id: value,
            }))
          } else {
            this.vasionForms = []
          }
        }
      } catch (err) {
        // Error handled silently
      } finally {
        this.loadingStates.vasionForms = false
      }
    },
    async fetchVasionWorkflows(tenantId) {
      this.loadingStates.vasionWorkflows = true
      try {
        const response = await get(`/v1/vasion/workflows?tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          if (response.data.data && response.data.data.Values) {
            this.workflows = response.data.data.Values
          } else {
            this.workflows = []
          }
        }
      } catch (err) {
        // Error handled silently
      } finally {
        this.loadingStates.vasionWorkflows = false
      }
    },
    async fetchVasionFolders(tenantId) {
      this.loadingStates.vasionFolders = true
      try {
        const response = await get(`/v1/vasion/folders?tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          this.storageFolders = response.data.data || []
          if (response.data.data && response.data.data.FolderList) {
            this.storageFolders = response.data.data.FolderList.Values
          }
        }
      } catch (err) {
        // Error handled silently
      } finally {
        this.loadingStates.vasionFolders = false
      }
    },
    // This method is now delegated to the tenant selection store
    // Keep it for backward compatibility but just call the store
    async checkTenantAiMethod(tenantId) {
      await tenantSelectionStore.checkTenantAiMethod(tenantId)
    },
    splitConfigValidations() {
      this.errors = {}
      let isValid = true
      // Page count validation
      if (this.newDocumentType.detection_mode === 'page_count') {
        if (!this.newDocumentType.page_number || this.newDocumentType.page_number <= 0) {
          this.errors.page_number = this.$t('configureVasionSettings.pageNumberGreaterThanZero')
          isValid = false
        }
      }

      // Splitting strategy validation for QR and Barcode
      if (this.newDocumentType.detection_mode === 'qr' || this.newDocumentType.detection_mode === 'barcode') {
        if (!this.newDocumentType.splitting_strategy) {
          this.errors.splitting_strategy = this.$t('configureVasionSettings.selectSplittingStrategy')
          isValid = false
        }
      }
      return isValid
    },
    validateForm() {
      this.errors = {}
      let isValid = true

      if (!this.newDocumentType.category_id) {
        this.errors.category_id = this.$t('documentTypes.forms.validation.primary_classification_required')
        isValid = false
      }

      if (!this.newDocumentType.department_id) {
        this.errors.department_id = this.$t('documentTypes.forms.validation.sub_category_required')
        isValid = false
      }

      if (!this.newDocumentType.ml_confidence_level) {
        this.errors.ml_confidence_level = this.$t('documentTypes.forms.validation.ml_confidence_level_required')
        isValid = false
      }

      if (this.newDocumentType.tenantId) {
        if (!this.newDocumentType.document_type_id) {
          this.errors.document_type_id = this.$t('documentTypes.forms.validation.document_type_required')
          isValid = false
        }
        if (!this.newDocumentType.vasion_form_id) {
          this.errors.vasion_form_id = this.$t('documentTypes.forms.validation.vasion_form_required')
          isValid = false
        }
        if (!this.newDocumentType.document_type_display_name) {
          this.errors.document_type_display_name = this.$t('documentTypes.forms.validation.display_name_required')
          isValid = false
        }
        // Only require workflow if send_to_vasion_storage is false (start workflow)
        if (!this.newDocumentType.send_to_vasion_storage && !this.newDocumentType.vasion_workflow_id) {
          this.errors.vasion_workflow_id = this.$t('documentTypes.forms.validation.workflow_name_required')
          isValid = false
        }
        if (!this.newDocumentType.vasion_folder_id) {
          this.errors.vasion_folder_id = this.$t('documentTypes.forms.validation.storage_folder_required')
          isValid = false
        }
      } else {
        if (!this.newDocumentType.document_type_name) {
          this.errors.document_type_name = this.$t('documentTypes.forms.validation.document_type_name_required')
          isValid = false
        }
        if (!this.selectedTenantUsesBedrock && !this.newDocumentType.model_id) {
          this.errors.model_id = this.$t('documentTypes.forms.validation.model_name_required')
          isValid = false
        }
      }

      return isValid
    },
    clearErrors() {
      this.errors = {}
      this.showErrors = false
    },
    clearFieldError(fieldName, value) {
      if (value && this.errors[fieldName]) {
        this.errors = {
          ...this.errors,
          [fieldName]: undefined,
        }
      }
    },
    validateField(fieldName, value) {
      if (this.showErrors) {
        this.clearFieldError(fieldName, value)
      }
    },
    async handleFieldChange(fieldName, value) {
      if (fieldName === 'category_id') {
        if (this.isPopulating) return

        if (value) {
          if (this.newDocumentType.tenantId) {
            await this.fetchTenantDepartments(this.newDocumentType.tenantId, value)
          } else {
            await this.fetchDepartmentsByCategory(value)
          }
          this.newDocumentType.department_id = ''
          this.newDocumentType.document_type_id = ''
          this.tenantDocumentTypes = []
        } else {
          this.departments = []
          this.tenantDocumentTypes = []
        }
        this.validateField('category_id', value)
      }

      if (fieldName === 'tenantId') {
        if (this.isPopulating) return

        if (value) {
          await Promise.all([this.fetchTenantCategories(value), this.fetchVasionForms(value), this.fetchVasionWorkflows(value), this.fetchVasionFolders(value)])

          this.newDocumentType.category_id = ''
          this.newDocumentType.department_id = ''
          this.newDocumentType.document_type_id = ''
          this.newDocumentType.vasion_form_id = ''
          this.newDocumentType.vasion_workflow_id = null
          this.newDocumentType.vasion_folder_id = ''
          this.newDocumentType.vasion_rejection_folder_id = ''
          this.newDocumentType.vasion_review_folder_id = ''
          this.newDocumentType.vasion_failed_folder_id = ''
          this.tenantDocumentTypes = []
          this.departments = []
        } else {
          this.fetchCategories()
          this.newDocumentType.category_id = ''
          this.newDocumentType.department_id = ''
          this.newDocumentType.document_type_name = ''
          this.newDocumentType.document_type_id = ''
          this.newDocumentType.document_type_display_name = ''
          this.newDocumentType.vasion_form_id = ''
          this.newDocumentType.vasion_workflow_id = null
          this.newDocumentType.vasion_folder_id = ''
          this.newDocumentType.vasion_rejection_folder_id = ''
          this.newDocumentType.vasion_review_folder_id = ''
          this.newDocumentType.vasion_failed_folder_id = ''
          this.newDocumentType.send_to_vasion_storage = false
          this.categories = []
          this.departments = []
          this.vasionForms = []
          this.workflows = []
          this.storageFolders = []
          this.tenantDocumentTypes = []
        }
        this.validateField('tenantId', value)
      }

      if (fieldName === 'department_id') {
        if (this.isPopulating) return
        this.newDocumentType.document_type_id = ''
        this.newDocumentType.document_type_display_name = ''
        this.tenantDocumentTypes = []
        await this.handleDepartmentChange(value)
        this.validateField('department_id', value)
      }
      if (fieldName === 'document_type_id') {
        if (this.isPopulating) return
        const selectedDocType = this.tenantDocumentTypes.find((doc) => doc.id === value)
        if (selectedDocType) {
          this.newDocumentType.document_type_display_name = selectedDocType.document_type_name
        }
      }

      this.validateField(fieldName, value)
    },
    handleAddDocumentType() {
      this.editMode = false
      this.editId = null
      this.resetForm()
      this.clearErrors()
      this.showAddForm = true

      // Set flag to prevent watcher conflicts during form initialization
      this._isFormInitialization = true

      // Initialize tenant from store (for Super Admin and others)
      this.newDocumentType.tenantId = this.selectedTenantId === 'master' ? '' : this.selectedTenantId

      // Clear the flag after a brief delay
      this.$nextTick(() => {
        this._isFormInitialization = false
      })

      this.fetchCategories()
      this.fetchModels()

      // If tenant is selected, fetch tenant-specific data
      if (this.newDocumentType.tenantId) {
        this.handleFieldChange('tenantId', this.newDocumentType.tenantId)
      }
    },
    async saveDocumentType() {
      this.showErrors = true
      if (!this.validateForm()) {
        showToast({
          title: this.$t('documentTypes.messages.error_title'),
          message: this.$t('documentTypes.forms.validation.fill_required_fields'),
          type: 'error',
        })
        return
      }

      this.loadingStates.saveDocumentType = true
      try {
        // Determine splitting configuration based on detection_mode
        let splitOnCode = false
        let detectionMode = null
        let splittingStrategy = null
        let pageNumber = 0

        if (this.newDocumentType.detection_mode === 'qr') {
          splitOnCode = true
          detectionMode = 'qr'
          splittingStrategy = this.newDocumentType.splitting_strategy || ''
          pageNumber = 0
        } else if (this.newDocumentType.detection_mode === 'barcode') {
          splitOnCode = true
          detectionMode = 'barcode'
          splittingStrategy = this.newDocumentType.splitting_strategy || ''
          pageNumber = 0
        } else if (this.newDocumentType.detection_mode === 'page_count') {
          splitOnCode = false
          detectionMode = 'page_count'
          splittingStrategy = null
          pageNumber = Number(this.newDocumentType.page_number) || 0
        } else {
          // 'auto' or default
          splitOnCode = false
          detectionMode = null
          splittingStrategy = null
          pageNumber = 0
        }
        if (!this.splitConfigValidations()) {
          showToast({
            title: this.$t('documentTypes.messages.error_title'),
            message: this.$t('documentTypes.forms.validation.fill_required_fields'),
            type: 'error',
          })
          return
        }

        const payload = {
          category_id: this.newDocumentType.category_id,
          department_id: this.newDocumentType.department_id,
          model_id: this.newDocumentType.model_id === '' ? null : this.newDocumentType.model_id,
          document_type_name: this.newDocumentType.document_type_name,
          ml_confidence_level: this.newDocumentType.ml_confidence_level,
          page_count: pageNumber > 0 ? Number(pageNumber) : 0,
          is_active: Boolean(this.newDocumentType.is_active),
          bedrock_class_name: this.newDocumentType?.bedrock_class_name,
          bda_blueprint: this.newDocumentType.bda_blueprint,
          bda_invocation_arn: this.newDocumentType.bda_invocation_arn,
          // Splitting Configuration - using existing fields
          split_on_code: splitOnCode,
          detection_mode: detectionMode,
          splitting_strategy: splittingStrategy,
          // page_count: pageNumber,
          // // Also send the input values for backend reference
          // splitting_type: this.newDocumentType.detection_mode || 'auto',
          // qr_splitting_type: this.newDocumentType.detection_mode === 'qr' ? this.newDocumentType.splitting_strategy || '' : '',
          // barcode_splitting_type: this.newDocumentType.detection_mode === 'barcode' ? this.newDocumentType.splitting_strategy || '' : '',
        }

        if (this.showVasionFields) {
          delete payload.document_type_name
          payload.document_type_id = this.newDocumentType.document_type_id
          payload.tenant_id = this.newDocumentType.tenantId
          payload.vasion_form_id = this.newDocumentType.vasion_form_id
          payload.document_type_display_name = this.newDocumentType.document_type_display_name
          payload.vasion_workflow_id = this.newDocumentType.vasion_workflow_id || null
          payload.vasion_folder_id = this.newDocumentType.vasion_folder_id
          if (this.newDocumentType.vasion_rejection_folder_id) {
            payload.vasion_rejection_folder_id = this.newDocumentType.vasion_rejection_folder_id
          } else {
            delete payload.vasion_rejection_folder_id
          }
          if (this.newDocumentType.vasion_review_folder_id) {
            payload.vasion_review_folder_id = this.newDocumentType.vasion_review_folder_id
          } else {
            delete payload.vasion_review_folder_id
          }
          if (this.newDocumentType.vasion_failed_folder_id) {
            payload.vasion_failed_folder_id = this.newDocumentType.vasion_failed_folder_id
          } else {
            delete payload.vasion_failed_folder_id
          }
          payload.send_to_vasion_storage = this.newDocumentType.send_to_vasion_storage == true ? true : false
          payload.requires_complete_manual_validation = this.newDocumentType.requires_complete_manual_validation
        }

        let response
        if (this.editMode) {
          payload.created_by = this.newDocumentType.created_by
          if (payload.tenant_id) {
            response = await put(`/v1/tenant-document-types/${this.editId}`, payload)
          } else {
            response = await put(`/v1/document-types/${this.editId}`, payload)
          }
        } else {
          if (payload.tenant_id) {
            response = await post('/v1/tenant-document-types', payload)
          } else {
            response = await post('/v1/document-types', payload)
          }
        }

        if (response?.data?.code === 200 || response?.data?.code === 201) {
          showToast({
            title: this.$t('documentTypes.messages.success_title'),
            message: this.editMode ? this.$t('documentTypes.messages.success.updated') : this.$t('documentTypes.messages.success.created'),
            type: 'success',
          })
          this.showAddForm = false
          this.editMode = false
          this.editId = null
          await this.fetchDocumentTypes()
        } else {
          throw new Error(response?.data?.message || (this.editMode ? this.$t('documentTypes.messages.error.update_failed') : this.$t('documentTypes.messages.error.create_failed')))
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message
        if (errorMessage.toLowerCase().includes('unique constraint')) {
          showToast({
            title: this.$t('documentTypes.messages.error_title'),
            message: this.$t('documentTypes.messages.error.duplicate_exists'),
            type: 'error',
          })
        } else {
          showToast({
            title: this.$t('documentTypes.messages.error_title'),
            message: err.message || (this.editMode ? this.$t('documentTypes.messages.error.update_failed') : this.$t('documentTypes.messages.error.create_failed')),
            type: 'error',
          })
        }
      } finally {
        this.loadingStates.saveDocumentType = false
      }
    },
    cancelAdd() {
      this.showAddForm = false
      this.editMode = false
      this.editId = null
      this.resetForm()
      this.clearErrors()
    },
    async populateFormData(docType) {
      this.isPopulating = true

      try {
        if (docType.tenant_id) {
          await Promise.all([this.fetchTenantCategories(docType.tenant_id), this.fetchVasionForms(docType.tenant_id), this.fetchVasionWorkflows(docType.tenant_id), this.fetchVasionFolders(docType.tenant_id), this.fetchTenantDepartments(docType.tenant_id, docType.category_id), await this.fetchTenantDocumentTypes(docType.category_id, docType.department_id)])
        } else {
          await this.fetchCategories()
          await this.fetchDepartmentsByCategory(docType.category_id)
          await this.fetchModels()
        }
        this.newDocumentType = {
          tenantId: docType.tenant_id || '',
          category_id: docType.category_id || '',
          department_id: docType.department_id || '',
          model_id: docType.model_id || '',
          document_type_name: docType.document_type_name || '',
          document_type_id: docType.document_type_id || '',
          ml_confidence_level: docType.ml_confidence_level || '',
          vasion_form_id: docType.vasion_form_id || '',
          document_type_display_name: docType.document_type_display_name || '',
          vasion_workflow_id: docType.vasion_workflow_id || null,
          vasion_folder_id: docType.vasion_folder_id || '',
          vasion_rejection_folder_id: docType.vasion_rejection_folder_id || '',
          vasion_review_folder_id: docType.vasion_review_folder_id || '',
          vasion_failed_folder_id: docType.vasion_failed_folder_id || '',
          send_to_vasion_storage: Boolean(docType.send_to_vasion_storage),
          is_active: Boolean(docType.is_active),
          created_by: docType.created_by || null,
          page_count: docType.page_count || 0,
          requires_complete_manual_validation: docType.requires_complete_manual_validation,
          bedrock_class_name: docType.bedrock_class_name || '',
          bda_blueprint: docType.bda_blueprint || '',
          // Splitting Configuration
          splitting_type: docType.splitting_type || 'none',
          page_number: docType.page_count || '',
          qr_splitting_type: docType.qr_splitting_type || 'static',
          barcode_splitting_type: docType.barcode_splitting_type || 'static',
          splitter_page_type: docType.splitter_page_type || '', // 'qr' or 'barcode' or empty
          // UI-facing mapping
          detection_mode: docType.detection_mode || 'auto',
          splitting_strategy: docType.splitting_strategy,
          bda_invocation_arn: docType.bda_invocation_arn || '',
        }
      } finally {
        this.$nextTick(() => {
          this.isPopulating = false
        })
      }
    },
    async handleDepartmentChange(newValue) {
      if (this.newDocumentType.tenantId && this.newDocumentType.category_id && newValue && !this.isPopulating) {
        await this.fetchTenantDocumentTypes(this.newDocumentType.category_id, newValue)
      }
    },
    resetForm() {
      // Initialize tenant from store for consistency
      const tenantIdFromStore = this.selectedTenantId === 'master' ? '' : this.selectedTenantId

      this.newDocumentType = {
        tenantId: tenantIdFromStore,
        category_id: '',
        department_id: '',
        vasion_form_id: '',
        model_id: '',
        document_type_name: '',
        document_type_id: '',
        document_type_display_name: '',
        ml_confidence_level: '',
        vasion_workflow_id: null,
        vasion_folder_id: '',
        vasion_rejection_folder_id: '',
        vasion_review_folder_id: '',
        vasion_failed_folder_id: '',
        send_to_vasion_storage: false,
        page_count: 0,
        is_active: true,
        created_by: null,
        bedrock_class_name: '',
        bda_blueprint: '',
        // Splitting Configuration
        detection_mode: 'auto',
        splitting_strategy: '',
        page_number: '',
        splitting_type: 'none',
        qr_splitting_type: 'static',
        barcode_splitting_type: 'static',
        splitter_page_type: '', // 'qr' or 'barcode' or empty
      }
      this.departments = []
      this.categories = []
      this.tenantDocumentTypes = []
      this.vasionForms = []
      this.workflows = []
      this.storageFolders = []
      this.models = []
    },
    editDocumentType(docType) {
      this.editMode = true
      this.editId = docType.id
      this.clearErrors()
      this.populateFormData(docType)
      this.showAddForm = true
      this.fetchBluePrintDetails()
    },
    async goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        await this.fetchDocumentTypes()
      }
    },
    async goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        await this.fetchDocumentTypes()
      }
    },
    handleStatusToggle(field, newValue) {
      this.pendingStatusChange = {
        field: { ...field },
        newStatus: newValue,
      }
      this.showConfirmModal = true
    },
    handleVasionStorageToggle(field, newValue) {
      this.pendingVasionStorageChange = {
        field: { ...field },
        newStatus: newValue,
      }
      this.showConfirmModal = true
    },
    closeConfirmModal() {
      this.showConfirmModal = false
      this.pendingStatusChange = null
      this.pendingVasionStorageChange = null
    },
    async confirmStatusChange() {
      if (this.pendingStatusChange) {
        const { field, newStatus } = this.pendingStatusChange
        const updatedField = this.documentTypes.find((f) => f.id === field.id)
        if (updatedField) {
          updatedField.is_active = newStatus
          await this.updateDocumentTypeStatus(updatedField)
        }
        this.closeConfirmModal()
        this.pendingStatusChange = null
      }

      if (this.pendingVasionStorageChange) {
        const { field, newStatus } = this.pendingVasionStorageChange
        const updatedField = this.documentTypes.find((f) => f.id === field.id)
        if (updatedField) {
          updatedField.send_to_vasion_storage = newStatus
          await this.updateDocumentTypeStatus(updatedField)
        }
        this.closeConfirmModal()
        this.pendingVasionStorageChange = null
      }
    },
    async updateDocumentTypeStatus(selectedDocumentType) {
      if (!selectedDocumentType) return
      try {
        const payload = selectedDocumentType

        let response
        if (payload.tenant_id) {
          response = await put(`/v1/tenant-document-types/${selectedDocumentType.id}`, payload)
        } else {
          response = await put(`/v1/document-types/${selectedDocumentType.id}`, payload)
        }
        if (response?.data?.code === 200 || response?.data?.code === 201) {
          showToast({
            title: this.$t('documentTypes.messages.success_title'),
            message: selectedDocumentType.is_active ? this.$t('documentTypes.messages.success.status_enabled') : this.$t('documentTypes.messages.success.status_disabled'),
            type: 'success',
          })
        } else {
          throw new Error(response?.data?.message || this.$t('documentTypes.messages.error.update_failed'))
        }
      } catch (err) {
        showToast({
          title: this.$t('documentTypes.messages.error_title'),
          message: err.message || this.$t('documentTypes.messages.error.update_failed'),
          type: 'error',
        })
      } finally {
        this.loadingStates.saveDocumentType = false
      }
    },
    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },
    async selectPageSize(size) {
      this.pageSize = size
      this.page = 1
      this.showPageSizeDropdown = false
      await this.fetchDocumentTypes()
    },
    handleFilterChange(filters) {
      this.activeFilters = filters
      this.page = 1
      this.fetchDocumentTypes()
    },
    handleStatusChange(value) {
      this.newDocumentType.is_active = value
    },
    handleManualValidationFlagChange(value) {
      this.newDocumentType.requires_complete_manual_validation = value
    },
    handleBedrockClassChange(event) {
      const selectedClass = event.target.value
      if (selectedClass) {
        const selectedBlueprint = this.blueprintDetails.find((blueprint) => blueprint.class === selectedClass)
        if (selectedBlueprint) {
          this.newDocumentType.bedrock_class_name = selectedBlueprint.class
          this.newDocumentType.bda_blueprint = selectedBlueprint.blueprintArn
        }
      } else {
        this.newDocumentType.bedrock_class_name = ''
        this.newDocumentType.bda_blueprint = ''
      }
      this.validateField('bedrock_class_name', selectedClass)
    },
    getConfirmationMessage() {
      if (this.pendingStatusChange) {
        const documentTypeName = this.pendingStatusChange.field?.document_type_name || this.pendingStatusChange.field?.document_type_display_name
        return this.pendingStatusChange.newStatus ? this.$t('documentTypes.modals.confirm_status.enable_message', { documentTypeName }) : this.$t('documentTypes.modals.confirm_status.disable_message', { documentTypeName })
      }
      if (this.pendingVasionStorageChange) {
        const documentTypeName = this.pendingVasionStorageChange.field?.document_type_name || this.pendingVasionStorageChange.field?.document_type_display_name
        return `Are you sure, you want to ${this.pendingVasionStorageChange.newStatus ? 'enable' : 'disable'} Send To Vasion Storage for ${documentTypeName}?`
      }
      return ''
    },

    /**
     * Calculate the number of table columns for proper colspan in no-data row
     */
    getTableColumnCount() {
      let count = 2 // Sr. No. and Tenant Name are always present

      if (this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) {
        count += 4 // Source File Name, Document Type Name, Classified As/Analysis Type, Status
        if (this.selectedTenantId !== 'master') {
          count += 1 // Training Actions
        }
      } else {
        count += 4 // Primary Classification, Sub Category, Document Type Name, Is Active
        if (this.writePermission) {
          count += 1 // Action
        }
      }

      return count
    },

    /**
     * Get the ai_method_filter parameter value based on tenant and user context
     * This replaces the frontend filtering logic
     * Handles Bedrock, Textract, and regular tenant filtering
     */
    getAiMethodFilterValue() {
      if (this.selectedTenantId === 'master') {
        // Master tenant: Exclude Bedrock Training and Bedrock Documents categories
        return 'master'
      } else {
        if (this.selectedTenantUsesBedrock) {
          // Bedrock tenant: Only include Bedrock Training category
          return 'bedrock'
        } else if (this.selectedTenantUsesTextract) {
          // Textract tenant: Only include Textract documents
          return 'textract'
        } else {
          // Non-Bedrock/non-Textract tenant: Exclude Bedrock/Textract documents
          return 'exclude_ai'
        }
      }
    },
    /**
     * Get the ai_method_filter parameter value based on tenant and user context
     * This replaces the frontend filtering logic
     * Handles Bedrock, Textract, and regular tenant filtering
     */
    getIncludeBedrockValue() {
      if (this.selectedTenantId === 'master') {
        // Master tenant: Exclude Bedrock Training and Bedrock Documents categories
        return false
      } else {
        if (this.selectedTenantUsesBedrock) {
          // Bedrock tenant: Only include Bedrock Training category
          return 'only'
        } else {
          // Non-Bedrock tenant: Exclude Bedrock Training category
          return false
        }
      }
    },
  },
  watch: {
    async selectedTenantId(newValue, oldValue) {
      // Only react if this is a manual change (not from store subscription)
      if (newValue === oldValue || this._isStoreUpdate) return

      this.page = 1
      this.activeFilters = null
      this.checkPermissions()

      // Clear existing document types data immediately
      this.documentTypes = []
      this.total = 0
      this.totalPages = 1

      // Update the store (will trigger AI method check via API call)
      await tenantSelectionStore.setSelectedTenantId(newValue)

      // Wait a bit for store to update, then fetch new data
      await this.fetchDocumentTypes()
    },
    async 'newDocumentType.tenantId'(value) {
      // Skip if this change is from store sync (during form initialization)
      if (this._isFormInitialization) {
        return
      }

      this.handleFieldChange('tenantId', value)

      // Since the dropdown is now always disabled, this watcher should only handle
      // field changes but not update the store (store is the source of truth)
    },
    'newDocumentType.category_id'(value) {
      this.handleFieldChange('category_id', value)
    },
    'newDocumentType.department_id'(value) {
      this.handleFieldChange('department_id', value)
    },
    'newDocumentType.document_type_name'(value) {
      this.validateField('document_type_name', value)
    },
    'newDocumentType.document_type_id'(value) {
      this.handleFieldChange('document_type_id', value)
    },
    'newDocumentType.vasion_form_id'(value) {
      this.validateField('vasion_form_id', value)
    },
    'newDocumentType.document_type_display_name'(value) {
      this.validateField('document_type_display_name', value)
    },
    'newDocumentType.vasion_workflow_id'(value) {
      this.validateField('vasion_workflow_id', value)
    },
    'newDocumentType.vasion_folder_id'(value) {
      this.validateField('vasion_folder_id', value)
    },
    'newDocumentType.model_id'(value) {
      this.validateField('model_id', value)
    },
    'newDocumentType.ml_confidence_level'(value) {
      this.validateField('ml_confidence_level', value)
    },
    'newDocumentType.bda_blueprint'() {
      // Optional: Add any additional validation for bda_blueprint if needed
    },
  },
}
</script>

<style scoped>
.searchBar {
  display: flex;
  align-items: center;
  width: 386px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dadada;
  background: #fff;
  padding: 0 10px;
  gap: 10px;
}

.searchInput {
  border: none;
  outline: none;
  width: 100%;
  font-family: Archivo;
  font-size: 14px;
  font-weight: 400;
  color: #8f969b;
}

.button-group {
  padding: 20px 0;
  margin-top: 0;
  display: flex;
  gap: 20px;
  border-top: 1px solid #e0e0e0;
}

.cancel-btn {
  padding: 10px 32px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  font-family: Archivo;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
}

.save-btn {
  padding: 10px 32px;
  background: var(--primary-color);
  color: white;
  font-family: Archivo;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 25px;
}

.form-field {
  width: 100%;
}

.form-field label {
  color: #8b8888;
  font-family: Archivo;
  font-size: 18px;
  font-weight: 400;
  display: block;
  margin-bottom: 6px;
}

.form-field input,
.form-field select {
  width: 100%;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.form-field select:disabled {
  background-color: #f5f5f5;
  color: #999999;
  cursor: not-allowed;
  opacity: 0.6;
}

.newPageTitle {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 28px;
  font-weight: 500;
  line-height: 150%;
}

.createDocumentTypeBody {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.createDocumentTypeHeader {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 9px;
}

.createDocumentTypeHeader {
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 9px;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.pageSizeDropdownNew {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  list-style: none;
  padding: 4px 0;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.pageSizeDropdownNew li {
  padding: 6px 12px;
  cursor: pointer;
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #5c656c;
}

.pageSizeDropdownNew li:hover {
  background: #f0f0f0;
}

.toggle-field {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 47px;
  padding: 0 12px;
}

.toggle-label {
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.error-border {
  border-color: #dc3545 !important;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  display: block;
  font-family: Archivo;
}

.truncate-cell {
  max-width: 250px;
  padding: 8px;
  position: relative;
}

.tooltip-container {
  position: relative;
  width: 100%;
}

.tooltip-container:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.tooltip {
  visibility: hidden;
  position: absolute;
  left: 0;
  top: 100%;
  background-color: #333;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.2s;
  word-break: break-all;
  white-space: normal;
  max-width: 300px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.truncate-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.truncate-text:hover {
  color: var(--primary-color);
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

.section-header {
  padding: 16px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
}

.section-title {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  margin-bottom: 4px;
}

.section-description {
  color: #5c656c;
  font-family: Archivo;
  font-size: 14px;
  margin: 0;
}

.section-content {
  padding: 8px;
}

.loading-container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  color: #666;
  font-family: Archivo;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.actions-menu {
  position: relative;
  display: inline-block;
  z-index: 10;
}

.actions-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.actions-button:hover {
  background-color: rgba(22, 72, 99, 0.1);
}

.actions-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-shadow: 0 4px 13.1px rgba(0, 0, 0, 0.1);
  z-index: 200000;
  min-width: 200px;
  padding: 8px 0;
}

.actions-dropup {
  top: auto;
  bottom: 100%;
  margin-bottom: 5px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 32px;
  cursor: pointer;
  color: #32393e;
  font-family: Archivo;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  transition: background-color 0.2s;
}

.action-item:hover {
  background-color: #f5f5f5;
}

.action-item svg {
  width: 20px;
  height: 20px;
  color: var(--primary-color);
}

.action-item span {
  white-space: nowrap;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid var(--primary-color, #007bff);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Splitting Configuration Styles */
.splitting-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.radio-input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.radio-label {
  font-family: Archivo;
  font-size: 16px;
  font-weight: 400;
  color: #32393e;
  cursor: pointer;
  margin: 0;
}

.conditional-field {
  margin-left: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
}

.sub-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.checkbox-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
}

.checkbox-input {
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin-top: 2px;
}

.checkbox-label {
  font-family: Archivo;
  font-size: 14px;
  font-weight: 400;
  color: #5c656c;
  cursor: pointer;
  margin: 0;
  line-height: 1.4;
}

/* Status Badge Styles */
.status-badge {
  display: inline-block;
  padding: 0px 12px;
  width: 90px;
  border-radius: 20px;
  font-family: Archivo, sans-serif;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 0.5px;
  border: 1px solid;
  transition: all 0.2s ease;
}

.status-published {
  background-color: #e8f5e8;
  color: #2e7d32;
  border-color: #4caf50;
}

.status-published:hover {
  background-color: #c8e6c9;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.2);
}

.status-draft {
  background-color: #fff3e0;
  color: #f57c00;
  border-color: #ff9800;
}

.status-draft:hover {
  background-color: #ffe0b2;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(255, 152, 0, 0.2);
}

/* Required field asterisk styling */
.required {
  color: #e74c3c;
  font-weight: bold;
  margin-left: 2px;
}
</style>
